import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import Datetime from 'react-datetime';
import SafeBtn from './shared/SafeBtn';
import 'react-datetime/css/react-datetime.css';
import DownloadBtn from './shared/DownloadBtn';
import Breadcrumbs from './Breadcrumbs';
import TimeDate from './TimeDate';

const PageContainer = ({
  children,
  mainAction,
  breadcrumbs,
  downloadXlsxUrl,
  downloadProdVVerdeUrl,
  downloadAllGarantiasXlsxUrl,
  downloadAllCreditosXlsxUrl,
  downloadReporteGrupoEconomico,
  downloadFilteredGarantiasXlsxUrl,
  downloadFilteredGarantiasXlsxUrlDisabled,
  reporteGrupoEconomicoDate,
  onReporteGrupoEconomicoDateChange,
}) => {
  const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);
  const reportePopoverRef = useRef(null);

  useEffect(() => {
    if (!isRangePickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        reportePopoverRef.current &&
        !reportePopoverRef.current.contains(event.target)
      ) {
        setIsRangePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isRangePickerOpen]);

  return (
    <div>
      <TimeDate />
      <div style={{ display: 'grid', gridTemplateColumns: '5fr auto auto auto auto auto auto' }}>
        <Breadcrumbs arrayMap={breadcrumbs} />
        <div style={{ paddingTop: '12px', gridColumnStart: 2, marginRight: '5px' }}>
          {downloadProdVVerdeUrl && (
            <DownloadBtn
              width="200px"
              className="btn btn-orsan"
              id="xlsx-download-vverde"
              text="Libro Prod. Operaciones"
              tipText="Descargar Producción VentaVerde"
              url={downloadProdVVerdeUrl}
              icon={faFileContract}
            />
          )}
        </div>
        <div style={{ paddingTop: '12px', gridColumnStart: 3, marginRight: '5px' }}>
          {downloadXlsxUrl && (
            <DownloadBtn
              className="btn btn-orsan"
              id="xlsx-download"
              text="Descargar Proyectos"
              tipText="Descargar Planilla Excel"
              url={downloadXlsxUrl}
            />
          )}
        </div>
        <div
          ref={reportePopoverRef}
          style={{
            paddingTop: '12px',
            gridColumnStart: 4,
            marginRight: '5px',
            position: 'relative',
          }}
        >
          {downloadReporteGrupoEconomico && (
            <>
              <SafeBtn
                loading={false}
                id="reporte-grupo-economico-toggle"
                className="btn btn-orsan"
                style={{ width: '210px', fontSize: '14px' }}
                onClick={() => setIsRangePickerOpen((prev) => !prev)}
              >
                <FontAwesomeIcon icon={faCalendarAlt} />
                &nbsp;&nbsp;
                Proyectos/Pólizas
              </SafeBtn>
              {isRangePickerOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    padding: '12px',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    width: '170px',
                    zIndex: 20,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Datetime
                      closeOnSelect
                      dateFormat="DD-MM-YYYY"
                      locale="es"
                      onChange={onReporteGrupoEconomicoDateChange}
                      inputProps={{ id: 'reporte-grupo-economico-date' }}
                      renderInput={(props) => (
                        <label htmlFor="reporte-grupo-economico-date" style={{ display: 'contents' }}>
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>Fecha de corte / Vigencia al</span>
                          <input
                            {...props}
                            className="rs-input"
                            style={{ width: '100%', fontSize: '12px' }}
                            placeholder="Selecciona la fecha de corte / Vigencia al"
                          />
                        </label>
                      )}
                      timeFormat={false}
                      value={reporteGrupoEconomicoDate}
                    />
                  </div>
                  <div
                    onClickCapture={() => setIsRangePickerOpen(false)}
                    style={{ width: '100%' }}
                  >
                    <DownloadBtn
                      width="100%"
                      className="btn btn-orsan"
                      id="xlsx-download-reporte-grupo-economico"
                      text="Descargar"
                      tipText="Descargar reporte de pólizas por grupo económico"
                      url={downloadReporteGrupoEconomico}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ paddingTop: '12px', gridColumnStart: 5, marginRight: '5px' }}>
          {downloadAllGarantiasXlsxUrl && (
            <DownloadBtn
              className="btn btn-orsan"
              id="xlsx-download-all-garantias"
              text="Reporte Pólizas"
              tipText="Descargar tabla con todas las pólizas"
              url={downloadAllGarantiasXlsxUrl}
            />
          )}
        </div>
        <div style={{ paddingTop: '12px', gridColumnStart: 6, marginRight: '5px' }}>
          {downloadFilteredGarantiasXlsxUrl && (
            <DownloadBtn
              className="btn btn-orsan"
              id="xlsx-download-filtered-garantias"
              text="Pólizas Filtradas"
              tipText={
                downloadFilteredGarantiasXlsxUrlDisabled
                  ? 'Seleccione un filtro para habilitar'
                  : 'Descargar tabla con las pólizas filtradas'
              }
              url={downloadFilteredGarantiasXlsxUrl}
              disabled={downloadFilteredGarantiasXlsxUrlDisabled}
            />
          )}
          {downloadAllCreditosXlsxUrl && (
            <DownloadBtn
              className="btn btn-orsan"
              id="xlsx-download-all-credito"
              text="Reporte Pólizas"
              tipText="Descargar tabla con todas las pólizas"
              url={downloadAllCreditosXlsxUrl}
            />
          )}
        </div>
        <div style={{ paddingTop: '12px', gridColumnStart: 7 }}>
          {mainAction && (
            <Link
              className="btn btn-orsan"
              disabled={false}
              to={mainAction.url}
              style={{ maxWidth: '300px', float: 'right', fontSize: '14px' }}
            >
              {mainAction.caption}
            </Link>
          )}
        </div>
      </div>
      {children}
      <div className="footer text-left">
        <div className="border-segments">
          <div className="segment" />
          <div className="segment" />
          <div className="segment" />
          <div className="segment" />
        </div>
        <div>{`© ${moment().year()} ORSAN. Encomenderos 113, Of. 801 Las Condes.`}</div>
      </div>
    </div>
  );
};

PageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  mainAction: PropTypes.shape({
    url: PropTypes.string,
    caption: PropTypes.string,
  }),
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
    }),
  ).isRequired,
  downloadXlsxUrl: PropTypes.string,
  downloadProdVVerdeUrl: PropTypes.string,
  downloadAllGarantiasXlsxUrl: PropTypes.string,
  downloadAllCreditosXlsxUrl: PropTypes.string,
  downloadReporteGrupoEconomico: PropTypes.string,
  downloadFilteredGarantiasXlsxUrl: PropTypes.string,
  downloadFilteredGarantiasXlsxUrlDisabled: PropTypes.bool,
  reporteGrupoEconomicoDate: PropTypes.string,
  onReporteGrupoEconomicoDateChange: PropTypes.func,
};

PageContainer.defaultProps = {
  mainAction: null,
  downloadXlsxUrl: '',
  downloadProdVVerdeUrl: '',
  downloadAllGarantiasXlsxUrl: '',
  downloadAllCreditosXlsxUrl: '',
  downloadReporteGrupoEconomico: '',
  downloadFilteredGarantiasXlsxUrl: '',
  downloadFilteredGarantiasXlsxUrlDisabled: true,
  reporteGrupoEconomicoDate: '',
  onReporteGrupoEconomicoDateChange: () => {},
};

export default PageContainer;
